<!-- src/views/timesheets/TimesheetHistoryView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">
        
        <!-- 🛡️ MASTER COMMAND HEADER -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 animate-fadeIn relative overflow-hidden text-left">
          <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
            <ClockIcon class="h-32 w-32 text-white" />
          </div>
          <div class="relative z-10 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6">
            <div>
              <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none text-left">Submission History</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4 text-left">
                Timesheet Audit Trail • {{ userStore.userProfile?.firstName }} {{ userStore.userProfile?.lastName }}
              </p>
            </div>
            
            <div class="flex flex-wrap items-center gap-4">
              <button 
                @click="showArchive = !showArchive" 
                class="px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg"
                :class="showArchive ? 'bg-[#20FCF1] text-[#0A1630]' : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'"
              >
                 <ArchiveBoxIcon class="h-4 w-4" /> 
                 <span>{{ showArchive ? 'Hide Archive' : 'Show Paid Records' }}</span>
              </button>

              <router-link :to="{ name: 'dashboard' }" class="bg-white/5 border border-white/10 text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 shadow-lg">
                 <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Dashboard</span>
              </router-link>
            </div>
          </div>
        </div>

        <!-- LOADING STATE -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-20">
          <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mb-6"></div>
          <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest text-center">Retrieving Secure Records...</p>
        </div>

        <!-- EMPTY STATE -->
        <div v-else-if="filteredSubmissions.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border-2 border-dashed border-slate-100 animate-fadeIn shadow-inner">
           <ClockIcon class="h-12 w-12 text-slate-300 mx-auto mb-6 opacity-30" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase text-center">Registry Empty</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2 max-w-sm mx-auto text-center">
             No historical logs detected for the current filter criteria.
           </p>
           <router-link to="/timesheets/new" class="mt-8 inline-block px-10 py-5 bg-[#20FCF1] text-[#0A1630] rounded-[20px] font-black uppercase text-[10px] tracking-widest shadow-xl hover:scale-105 transition-all">
             Submit New Log
           </router-link>
        </div>

        <!-- HISTORY AUDIT LIST -->
        <div v-else class="grid grid-cols-1 gap-6 animate-fadeIn text-left">
          <div 
            v-for="sub in filteredSubmissions" 
            :key="sub.id" 
            class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl transition-all overflow-hidden group cursor-pointer"
            :class="expandedId === sub.id ? 'ring-2 ring-[#20FCF1]/30' : 'hover:border-[#20FCF1]/30'"
            @click="toggleExpand(sub.id)"
          >
            <div class="p-6 sm:p-8 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
              
              <div class="flex items-center gap-6 flex-grow text-left">
                <!-- WEEK INDICATOR -->
                <div class="h-20 w-20 bg-[#0A1630] rounded-2xl flex flex-col items-center justify-center border-b-4 border-[#20FCF1] shrink-0 shadow-lg group-hover:scale-105 transition-transform">
                  <span class="text-[8px] font-black text-[#20FCF1] uppercase tracking-tighter">WEEK</span>
                  <span class="text-3xl font-black text-white leading-none">{{ getVal(sub, 'railWeek') }}</span>
                </div>
                
                <div class="flex-grow min-w-0 text-left">
                  <!-- JOB REFERENCE REGISTRY -->
                  <div class="flex flex-wrap items-center gap-3 mb-6 text-left">
                    <span class="bg-[#0A1630] text-[#20FCF1] text-[9px] font-black px-3 py-1 rounded-lg uppercase tracking-widest">
                      {{ sub.humanReadableId || 'Registry' }}
                    </span>
                    <div class="flex flex-wrap items-center gap-1.5 max-w-full overflow-hidden text-left">
                       <span v-for="job in resolveUniqueJobs(sub)" :key="job" class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter bg-slate-50 px-2.5 py-1 rounded border border-slate-100">
                          {{ job }}
                       </span>
                    </div>
                  </div>
                  
                  <div class="flex items-center gap-4 text-left">
                    <div class="flex items-center gap-2">
                       <div class="w-2 h-2 rounded-full bg-[#20FCF1]"></div>
                       <span class="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                         {{ getVal(sub, 'department') || 'General Rail' }}
                       </span>
                    </div>
                    <div class="bg-slate-50 px-3 py-1 rounded-lg border border-slate-100">
                      <span class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">
                        {{ getVal(sub, 'totalHours') || 0 }} HRS Total
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- STATUS & AUDIT SIGNATURE -->
              <div class="flex flex-col lg:items-end gap-3 w-full lg:w-auto border-t lg:border-t-0 border-slate-50 pt-6 lg:pt-0 text-left lg:text-right">
                <div v-if="getVal(sub, 'managerAuthorisedBy')" class="flex flex-col items-start lg:items-end mb-1">
                  <span class="text-[8px] font-black text-slate-300 uppercase tracking-[0.2em] mb-1">Authorised By</span>
                  <span class="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-2 bg-slate-50 px-3 py-1 rounded-lg border border-slate-100 shadow-sm">
                    <CheckBadgeIcon class="h-3 w-3 text-[#20FCF1]" />
                    {{ getVal(sub, 'managerAuthorisedBy') }}
                  </span>
                </div>
                <div :class="getStatusStyles(getVal(sub, 'status'))" class="px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-[0.15em] border shadow-sm text-center min-w-[160px]">
                  {{ getVal(sub, 'status') || 'Pending' }}
                </div>
              </div>
            </div>

            <!-- EXPANDABLE DETAILS AREA -->
            <Transition 
              enter-active-class="transition duration-300 ease-out" 
              enter-from-class="transform opacity-0 -translate-y-4" 
              enter-to-class="transform opacity-100 translate-y-0" 
              leave-active-class="transition duration-200 ease-in" 
              leave-from-class="transform opacity-100 translate-y-0" 
              leave-to-class="transform opacity-0 -translate-y-4"
            >
              <div v-if="expandedId === sub.id" class="bg-slate-50/80 p-8 sm:p-10 border-t border-slate-100 text-left" @click.stop>
                
                <!-- MANAGEMENT FEEDBACKS -->
                <div v-if="isQueried(sub)" class="mb-10 p-8 bg-white border-l-8 border-indigo-600 rounded-[32px] shadow-lg animate-fadeIn text-left">
                    <div class="flex items-center gap-4 mb-4 text-left">
                        <div class="bg-indigo-50 p-2 rounded-xl"><InformationCircleIcon class="h-6 w-6 text-indigo-600" /></div>
                        <h4 class="text-sm font-black text-indigo-700 uppercase tracking-widest">Management Query: Action Required</h4>
                    </div>
                    <p class="text-sm font-medium text-slate-600 leading-relaxed bg-indigo-50/30 p-6 rounded-2xl italic border border-indigo-100/50">
                        "{{ getVal(sub, 'queryNotes') || 'Instructions pending. Please contact management.' }}"
                    </p>
                    <div class="mt-6 flex items-center justify-between">
                        <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest">Raised By: {{ getVal(sub, 'queriedBy') || 'Department Manager' }}</p>
                        <router-link to="/timesheets/new" class="text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:underline decoration-2 underline-offset-4">Update & Resubmit Log &rarr;</router-link>
                    </div>
                </div>

                <div v-if="isDeclined(sub)" class="mb-10 p-8 bg-white border-l-8 border-rose-500 rounded-[32px] shadow-lg animate-fadeIn text-left">
                    <div class="flex items-center gap-4 mb-4 text-left">
                        <div class="bg-rose-50 p-2 rounded-xl"><ExclamationTriangleIcon class="h-6 w-6 text-rose-500" /></div>
                        <h4 class="text-sm font-black text-rose-600 uppercase tracking-widest">Management Feedback: Correction Required</h4>
                    </div>
                    <p class="text-sm font-medium text-slate-600 leading-relaxed bg-rose-50/30 p-6 rounded-2xl italic border border-rose-100/50">
                        "{{ getVal(sub, 'rejectionNotes') || 'No specific feedback provided. Please contact your manager.' }}"
                    </p>
                    <div class="mt-6 flex items-center justify-between">
                        <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest">Actioned By: {{ getVal(sub, 'managerActionBy') || 'Department Manager' }}</p>
                        <router-link to="/timesheets/new" class="text-[10px] font-black text-rose-500 uppercase tracking-widest hover:underline decoration-2 underline-offset-4">Submit Corrected Log &rarr;</router-link>
                    </div>
                </div>

                <!-- DAILY BREAKDOWN -->
                <h4 class="text-[10px] font-black uppercase tracking-[0.3em] text-[#0A1630] mb-6 ml-1 text-left">Daily Periodic Breakdown</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4 text-left">
                  <div v-for="day in (getVal(sub, 'days') || [])" :key="day.isoDate" class="bg-white p-5 rounded-[24px] border border-slate-200 shadow-sm flex flex-col text-left">
                    <p class="text-[9px] font-black text-slate-400 uppercase mb-3 tracking-widest border-b border-slate-50 pb-2 text-left">{{ day.dayName }}</p>
                    
                    <div v-if="!day.isOff && day.entries?.length" class="space-y-4 flex-grow text-left">
                      <div v-for="(entry, eIdx) in day.entries" :key="eIdx" class="border-l-2 border-[#20FCF1] pl-3 text-left">
                        <p class="text-xl font-black text-[#0A1630] leading-none text-left">
                          {{ entry.hours }}<span class="text-[9px] text-slate-300 ml-0.5 font-bold">H</span>
                        </p>
                        <p class="text-[8px] font-black text-[#20FCF1] uppercase truncate mt-1.5 tracking-tighter text-left">
                          {{ entry.jobNumber || 'NO REF' }}
                        </p>
                        <p class="text-[7px] font-bold text-slate-400 uppercase truncate tracking-widest mt-0.5">
                          {{ entry.department || 'General Rail' }}
                        </p>
                      </div>
                    </div>
                    
                    <div v-else class="flex-grow flex items-center justify-center opacity-10 py-6">
                      <MoonIcon class="h-6 w-6 text-[#0A1630]" />
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { db, auth } from '@/firebaseConfig';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { 
  ClockIcon, ArrowUturnLeftIcon, CheckBadgeIcon, 
  ArchiveBoxIcon, MoonIcon, ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const submissions = ref([]);
const loading = ref(true);
const showArchive = ref(false);
const expandedId = ref(null);

const getVal = (sub, key) => {
  if (!sub) return null;
  return sub.formData?.[key] ?? sub[key] ?? null;
};

const isDeclined = (sub) => {
    const s = (getVal(sub, 'status') || '').toLowerCase();
    return s.includes('declined');
};

const isQueried = (sub) => {
    const s = (getVal(sub, 'status') || '').toLowerCase();
    return s.includes('queried');
};

const toggleExpand = (id) => {
    expandedId.value = expandedId.value === id ? null : id;
};

const resolveUniqueJobs = (sub) => {
  const jobSet = new Set();
  const days = getVal(sub, 'days') || [];
  days.forEach(day => {
    if (day.entries && Array.isArray(day.entries)) {
      day.entries.forEach(entry => {
        if (entry.jobNumber && entry.jobNumber.trim()) {
          jobSet.add(entry.jobNumber.trim());
        }
      });
    }
  });
  return Array.from(jobSet);
};

const filteredSubmissions = computed(() => {
  return submissions.value.filter(sub => {
    const status = (getVal(sub, 'status') || '').toLowerCase();
    if (showArchive.value) return true;
    return status !== 'paid' && status !== 'authorised for payroll' && status !== 'voided';
  });
});

const fetchHistory = async () => {
  loading.value = true;
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    
    const q = query(
      collection(db, 'timesheetSubmissions'), 
      where('userId', '==', userId), 
      orderBy('submittedAt', 'desc')
    );
    
    const snap = await getDocs(q);
    submissions.value = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (err) { 
    console.error("Registry Audit Retrieval Error:", err); 
  } finally { 
    loading.value = false; 
  }
};

const getStatusStyles = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'paid' || s.includes('authorised for payroll')) return 'bg-[#EBFDF8] text-[#20FCF1] border-[#20FCF1]/20';
  if (s.includes('authorised')) return 'bg-[#EBFDF8] text-[#20FCF1] border-[#20FCF1]/20';
  if (s.includes('pending')) return 'bg-amber-50 text-amber-700 border-amber-100';
  if (s.includes('declined')) return 'bg-rose-50 text-rose-600 border-rose-100';
  if (s.includes('queried')) return 'bg-indigo-50 text-indigo-600 border-indigo-100';
  if (s.includes('void')) return 'bg-slate-100 text-slate-400 border-slate-200';
  return 'bg-slate-50 text-slate-400 border-slate-100';
};

onMounted(() => { 
  if (userStore.isAuthReady) fetchHistory();
  else {
    const unwatch = userStore.$subscribe(() => {
      if (userStore.isAuthReady) { 
        fetchHistory(); 
        unwatch(); 
      }
    });
  }
});
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
.no-scrollbar::-webkit-scrollbar { display: none; }
</style>